<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import ContactAssignmentsAPI from 'dashboard/api/contactAssignments';

import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactPickerDialog from 'dashboard/components-next/Contacts/ContactPickerDialog.vue';

const { t } = useI18n();
const store = useStore();
const agents = useMapGetter('agents/getVerifiedAgents');
const teams = useMapGetter('teams/getTeams');

const assignments = ref([]);
const removedIds = ref([]);
const isFetching = ref(false);
const isSaving = ref(false);
const contactPickerRef = ref(null);
const pickingAssignment = ref(null);

const targetOptions = computed(() => [
  { label: t('CONTACT_ASSIGNMENTS.TARGET_AGENT'), value: 'agent' },
  { label: t('CONTACT_ASSIGNMENTS.TARGET_TEAM'), value: 'team' },
]);

const assigneeOptions = target => {
  const list = target === 'team' ? teams.value : agents.value;
  return (list || []).map(item => ({ label: item.name, value: item.id }));
};

const contactLabel = contact =>
  [contact.name, contact.phone_number].filter(Boolean).join(' · ');

let rowKey = 0;
const buildRow = (target = 'agent', assigneeId = null) => {
  rowKey += 1;
  return { key: rowKey, contacts: [], target, assigneeId };
};

// Each rule is stored per contact; a row groups the contacts that share target and assignee.
const fetchAssignments = async () => {
  isFetching.value = true;
  try {
    const { data } = await ContactAssignmentsAPI.get();
    const rows = new Map();
    data.forEach(item => {
      const target = item.team_id ? 'team' : 'agent';
      const assigneeId = item.team_id || item.agent_id;
      const groupKey = `${target}-${assigneeId}`;
      if (!rows.has(groupKey)) rows.set(groupKey, buildRow(target, assigneeId));
      rows.get(groupKey).contacts.push({
        id: item.contact_id,
        assignmentId: item.id,
        label: contactLabel({
          name: item.contact_name,
          phone_number: item.contact_phone_number,
        }),
      });
    });
    assignments.value = [...rows.values()];
  } catch (error) {
    useAlert(t('CONTACT_ASSIGNMENTS.LOAD_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const addRow = () => {
  assignments.value.push(buildRow());
};

const markRemoved = contact => {
  if (contact.assignmentId) removedIds.value.push(contact.assignmentId);
};

const removeRow = index => {
  assignments.value[index].contacts.forEach(markRemoved);
  assignments.value.splice(index, 1);
};

const removeContact = (row, contact) => {
  markRemoved(contact);
  row.contacts = row.contacts.filter(item => item.id !== contact.id);
};

const openContactPicker = assignment => {
  pickingAssignment.value = assignment;
  contactPickerRef.value?.open();
};

// Picked contacts join the row; contacts already in any row are skipped.
const onContactPicked = pickedContacts => {
  const usedIds = new Set(
    assignments.value.flatMap(row => row.contacts.map(contact => contact.id))
  );
  pickingAssignment.value.contacts.push(
    ...pickedContacts
      .filter(contact => !usedIds.has(contact.id))
      .map(contact => ({
        id: contact.id,
        assignmentId: null,
        label: contactLabel(contact),
      }))
  );
};

const onTargetChange = (assignment, target) => {
  assignment.target = target;
  assignment.assigneeId = null;
};

const toPayload = (row, contact) => ({
  contact_assignment: {
    contact_id: contact.id,
    agent_id: row.target === 'agent' ? row.assigneeId : null,
    team_id: row.target === 'team' ? row.assigneeId : null,
  },
});

const saveAssignments = async () => {
  if (assignments.value.some(row => !row.contacts.length || !row.assigneeId)) {
    useAlert(t('CONTACT_ASSIGNMENTS.VALIDATION.REQUIRED'));
    return;
  }

  isSaving.value = true;
  try {
    // Deletes first so a contact moved between rows doesn't hit the unique index
    await Promise.all(
      removedIds.value.map(id => ContactAssignmentsAPI.delete(id))
    );
    await Promise.all(
      assignments.value.flatMap(row =>
        row.contacts.map(contact =>
          contact.assignmentId
            ? ContactAssignmentsAPI.update(
                contact.assignmentId,
                toPayload(row, contact)
              )
            : ContactAssignmentsAPI.create(toPayload(row, contact))
        )
      )
    );
    removedIds.value = [];
    await fetchAssignments();
    useAlert(t('CONTACT_ASSIGNMENTS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('CONTACT_ASSIGNMENTS.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  fetchAssignments();
});
</script>

<template>
  <div class="flex flex-col w-full">
    <div class="flex items-center justify-end gap-2 px-4 pt-4">
      <Button
        :label="t('CONTACT_ASSIGNMENTS.ADD_ROW')"
        icon="i-lucide-plus"
        variant="outline"
        color="slate"
        size="sm"
        @click="addRow"
      />
      <Button
        :label="t('CONTACT_ASSIGNMENTS.SAVE')"
        icon="i-lucide-save"
        size="sm"
        :is-loading="isSaving"
        @click="saveAssignments"
      />
    </div>

    <div class="flex flex-col gap-4 p-4">
      <p class="text-sm text-n-slate-11">
        {{ t('CONTACT_ASSIGNMENTS.DESCRIPTION') }}
      </p>

      <div
        v-if="isFetching"
        class="flex items-center gap-2 text-sm text-n-slate-11"
      >
        <Spinner />
        <span>{{ t('CONTACT_ASSIGNMENTS.LOADING') }}</span>
      </div>

      <div
        v-else-if="!assignments.length"
        class="p-4 text-sm border border-dashed rounded-md border-n-weak text-n-slate-11"
      >
        {{ t('CONTACT_ASSIGNMENTS.EMPTY_STATE') }}
      </div>

      <div v-else class="flex flex-col gap-3">
        <div
          class="hidden md:grid grid-cols-[minmax(0,1fr)_200px_260px_auto] gap-3 text-xs font-semibold uppercase text-n-slate-11"
        >
          <span>{{ t('CONTACT_ASSIGNMENTS.CONTACT_LABEL') }}</span>
          <span>{{ t('CONTACT_ASSIGNMENTS.TARGET_LABEL') }}</span>
          <span>{{ t('CONTACT_ASSIGNMENTS.ASSIGNEE_LABEL') }}</span>
        </div>
        <div
          v-for="(assignment, index) in assignments"
          :key="assignment.key"
          class="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_200px_260px_auto] gap-3 items-center"
        >
          <div
            class="flex flex-wrap items-center gap-1.5 min-h-10 px-2 py-1.5 rounded-lg outline outline-1 outline-n-weak"
          >
            <span
              v-for="contact in assignment.contacts"
              :key="contact.id"
              class="inline-flex items-center gap-1 max-w-full py-0.5 ltr:pl-2 ltr:pr-1 rtl:pr-2 rtl:pl-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-12"
            >
              <span class="truncate">{{ contact.label }}</span>
              <Button
                icon="i-lucide-x"
                variant="ghost"
                color="slate"
                size="xs"
                @click="removeContact(assignment, contact)"
              />
            </span>
            <Button
              :label="t('CONTACT_ASSIGNMENTS.CHOOSE_CONTACT')"
              icon="i-lucide-user-plus"
              variant="link"
              color="blue"
              size="xs"
              @click="openContactPicker(assignment)"
            />
          </div>
          <ComboBox
            :model-value="assignment.target"
            :options="targetOptions"
            @update:model-value="value => onTargetChange(assignment, value)"
          />
          <ComboBox
            v-model="assignment.assigneeId"
            :options="assigneeOptions(assignment.target)"
            :placeholder="t('CONTACT_ASSIGNMENTS.CHOOSE_ASSIGNEE')"
          />
          <Button
            v-tooltip.top="t('CONTACT_ASSIGNMENTS.REMOVE')"
            icon="i-lucide-trash-2"
            variant="ghost"
            color="ruby"
            size="sm"
            @click="removeRow(index)"
          />
        </div>
      </div>
    </div>
    <ContactPickerDialog
      ref="contactPickerRef"
      multiple
      :title="t('CONTACT_ASSIGNMENTS.CHOOSE_CONTACT')"
      @confirm="onContactPicked"
    />
  </div>
</template>
